import { useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ServiceIcon } from "@/components/ServiceIcon";
import { CategoryDialog, DeleteCategoryDialog } from "@/components/crm/admin/CategoryDialogs";

export const CategoriesPanel = ({ categories, reload }) => {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const move = async (i, d) => {
    const ids = categories.map((c) => c.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { await api.put("/admin/service-categories/reorder", { ids }); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/admin/service-categories/${c.id}`, { active: !c.active }); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid="categories-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">El orden define cómo aparecen en el sitio. Las categorías sin servicios activos no se muestran al público.</p>
        <Button onClick={() => setEditing({})} className="rounded-xl bg-[#1E6FD9]" data-testid="new-category-button"><Plus className="mr-1.5 h-4 w-4" /> Nueva categoría</Button>
      </div>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200/80 bg-white" data-testid="categories-list">
        {categories.map((c, i) => (
          <li key={c.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 ${c.active ? "" : "opacity-60"}`} data-testid={`category-row-${c.slug}`}>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#1E6FD9]/10 text-[#1E6FD9]"><ServiceIcon name={c.icon} className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-0">
              <p className="truncate font-semibold text-[#0B2545]">{c.name}</p>
              <p className="truncate text-xs text-slate-500">{c.service_count} servicio(s){c.description ? ` · ${c.description}` : ""}</p>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Subir ${c.name}`} data-testid={`category-up-${c.slug}`}><ArrowUp className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" disabled={i === categories.length - 1} onClick={() => move(i, 1)} aria-label={`Bajar ${c.name}`} data-testid={`category-down-${c.slug}`}><ArrowDown className="h-4 w-4" /></Button>
              <Switch checked={c.active} onCheckedChange={() => toggle(c)} aria-label={`Activar ${c.name}`} className="mx-1" data-testid={`category-active-${c.slug}`} />
              <Button variant="ghost" size="icon" onClick={() => setEditing(c)} aria-label={`Editar ${c.name}`} data-testid={`category-edit-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setDeleting(c)} aria-label={`Eliminar ${c.name}`} className="text-rose-600 hover:text-rose-700" data-testid={`category-delete-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </li>
        ))}
        {!categories.length && <li className="px-4 py-6 text-sm text-slate-500">Aún no hay categorías.</li>}
      </ul>
      <CategoryDialog category={editing} onClose={() => setEditing(null)} onSaved={reload} />
      <DeleteCategoryDialog category={deleting} categories={categories} onClose={() => setDeleting(null)} onDone={reload} />
    </div>
  );
};
